/** @jsxImportSource @emotion/react */

import { useMutation } from '@apollo/client'
import { FC, useMemo } from 'react'
import { TAKE_ALL_ITEMS_COUNT } from 'constants/consts'

import { createCheatSheet, createCheatSheetVariables } from '__generatedTypes__/createCheatSheet'
import { findAllCheatSheet_findAllCheatSheet_items as ICheatSheet } from '__generatedTypes__/findAllCheatSheet'
import { findAllCheatSheetGroup_findAllCheatSheetGroup_items as IGroup } from '__generatedTypes__/findAllCheatSheetGroup'
import { removeCheatSheet, removeCheatSheetVariables } from '__generatedTypes__/removeCheatSheet'
import { updateCheatSheet, updateCheatSheetVariables } from '__generatedTypes__/updateCheatSheet'

import client from 'configs/apolloClient'
import { CREATE_CHEAT_SHEET, DELETE_CHEAT_SHEET, FIND_ALL_CHEAT_SHEET, UPDATE_CHEAT_SHEET } from 'queries/cheatSheet'
import { FieldValues } from 'utils/types'
import { toast } from 'utils/toast'
import { MembershipType } from '__generatedTypes__/globalTypes'
import {
  GOOGLE_DRIVE_TYPE,
  BOOSTER_ENV_TYPE,
  URLCardForm,
  UpdateCardData,
  getNoteEnvironmentItems,
} from 'components/URLCardForm'
import { usePlatformsAndSubjects } from 'hooks/usePlatformsAndSubjects'
import * as classes from 'components/shared/GroupCards/commonStyles'
import { getNoteEnvironmentConfig, isLandingPageAvailable } from '../platformConfig'

interface IProps {
  cheatSheet: ICheatSheet | { id: string; cheatSheetGroup: { id: string }; membershipLevel?: MembershipType }
  onClose: () => void
  cheatSheetGroups: IGroup[]
  cheatSheetLength: number
  isEditMode: boolean
}

const ManageCheatSheetsModal: FC<IProps> = ({
  cheatSheet,
  onClose,
  cheatSheetLength,
  cheatSheetGroups,
  isEditMode,
}) => {
  const { selectedPlatformSlug } = usePlatformsAndSubjects({})
  const noteEnvironmentItems = getNoteEnvironmentItems(getNoteEnvironmentConfig(selectedPlatformSlug))
  const [updateCheatSheet, { loading: updateLoading }] = useMutation<updateCheatSheet, updateCheatSheetVariables>(
    UPDATE_CHEAT_SHEET
  )
  const [removeCheatSheet] = useMutation<removeCheatSheet, removeCheatSheetVariables>(DELETE_CHEAT_SHEET)

  const handleCloseAndUpdate = () => {
    client
      .refetchQueries({
        include: [FIND_ALL_CHEAT_SHEET],
      })
      .then(onClose)
  }

  const [createCheatSheet, { loading: createLoading }] = useMutation<createCheatSheet, createCheatSheetVariables>(
    CREATE_CHEAT_SHEET
  )

  const handleGroupRemove = async () => {
    if (!cheatSheet?.id) return
    try {
      await removeCheatSheet({ variables: { params: cheatSheet.id } })
      handleCloseAndUpdate()

      toast.success('Card deleted successfully')
    } catch (error) {
      toast.error('Failed to delete card')
    }
  }

  const handleUpdateCard = async (data: Partial<UpdateCardData>) => {
    await updateCheatSheet({
      variables: {
        params: {
          id: data.id,
          title: data.name,
          description: data.description,
          landingPageDescription: data.landingPageDescription,
          link: data.link,
          membershipLevel: data.membershipLevel,
          ...(data.group?.id && { cheatSheetGroup: { id: data.group.id } }),
          isBoosterEnvironment: data.isBoosterEnvironmentUpd,
          isDocumentUpdated: data.isDocumentUpdated,
        },
      },
      refetchQueries: () => [
        {
          query: FIND_ALL_CHEAT_SHEET,
          variables: {
            query: {
              take: TAKE_ALL_ITEMS_COUNT,
              where: {
                cheatSheetGroup: {
                  id: data.group?.id,
                },
              },
            },
          },
        },
      ],
    })
    handleCloseAndUpdate()
  }

  const handleCreateCard = async (data: FieldValues) => {
    await createCheatSheet({
      variables: {
        params: {
          title: data.title || data.name,
          description: data.description,
          landingPageDescription: data.landingPageDescription,
          order: cheatSheetLength,
          link: data.link,
          membershipLevel: data.membershipLevel,
          cheatSheetGroup: {
            id: data.group?.id,
          },
          isBoosterEnvironment: data.noteEnvironment?.id === BOOSTER_ENV_TYPE.id,
        },
      },
    })
    handleCloseAndUpdate()
  }

  const initialValues = useMemo(
    () => ({
      ...(isEditMode
        ? {
            ...cheatSheet,
            name: 'title' in cheatSheet ? cheatSheet.title : '',
            group: {
              id: cheatSheet.cheatSheetGroup?.id,
            },
            membershipLevel: cheatSheet.membershipLevel,
          }
        : cheatSheet),
      noteEnvironment: (cheatSheet as ICheatSheet).isBoosterEnvironment ? BOOSTER_ENV_TYPE : GOOGLE_DRIVE_TYPE,
    }),
    [cheatSheet]
  )

  return (
    <div css={classes.modalContainer}>
      <URLCardForm<ICheatSheet>
        title={isEditMode ? 'Edit Card' : 'Create Card'}
        onGroupRemove={handleGroupRemove}
        groups={cheatSheetGroups}
        onClose={onClose}
        isEditMode={isEditMode}
        card={cheatSheet as ICheatSheet}
        updateCard={handleUpdateCard}
        createCard={handleCreateCard}
        isLoading={updateLoading || createLoading}
        initialValues={initialValues}
        noteEnvironmentItems={noteEnvironmentItems}
        hasLandingPage={isLandingPageAvailable(selectedPlatformSlug)}
      />
    </div>
  )
}

export default ManageCheatSheetsModal
